import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, tap, map, forkJoin } from "rxjs";
import { AuthenticationService } from "./authentication.service";
import { LoaderService } from "./loader.service";
import { EnvironmentService } from "./environment.service";
import { RaceSettingsService } from "./race-settings.service";
import { ApplicationFormSettingsService } from "./application-form-settings.serivce";
import {
  ActionResult,
  ActionResultList,
  FetchRaceByIdRequest,
  Race,
  RaceRegistrationRequestDto,
  RaceStatus,
  UserDetail,
  RaceRegistrationResultDto,
  PagedFilter,
  RegistrationFilter,
  RaceRegistration,
  EventItem,
} from "@model";

@Injectable({ providedIn: 'root' })
export class RaceService {
  public static readonly STARTS_EVERY_EDIT_NOT_ALLOWED: number = 10;
  public static readonly SPORT_EDIT_NOT_ALLOWED: number = 11;

  private _currentRace = new BehaviorSubject<Race | undefined>(undefined);
  private _races = new BehaviorSubject<ActionResultList<Race[], number> | null>(null);
  private _registerToRaceUserData = new BehaviorSubject<UserDetail | null>(null);

  get currentRace() { return this._currentRace; }
  get races() { return this._races; }
  get registerToRaceUserData() { return this._registerToRaceUserData; }

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private loaderService: LoaderService,
    private environmentService: EnvironmentService,
    private raceSettingsService: RaceSettingsService,
    private applicationFormSettingsService: ApplicationFormSettingsService
  ) {}

  setCurrentRace = (race: Race) => this.currentRace.next(race);

  copyRace = (race: Race, onSuccess: (raceId: number) => void, onError: () => void) =>
    this.http.post<ActionResult<number>>(
      `${this.environmentService.getApiUrl()}/api/copy/race`, race
    ).subscribe(actionResult => actionResult.resultCode !== -1 && actionResult.result ? onSuccess(actionResult.result) : onError());

  updateRace = (race: Race, onSuccess: (raceId: number) => void, onError: (errorCode?: number | undefined) => void) =>
    this.http.post<ActionResult<number>>(
      `${this.environmentService.getApiUrl()}/${race.id ? "api/races/update" : "api/races/add"}`, race
    ).pipe(tap(() => this.loaderService.dismissLoading())).subscribe(actionResult =>
        (actionResult.resultCode !== -1 && actionResult.resultCode !== RaceService.STARTS_EVERY_EDIT_NOT_ALLOWED) && actionResult.result ?
            onSuccess(actionResult.result) : onError(actionResult.resultCode));

  deleteRace = (race: Race, onSuccess: () => void, onError: () => void) => {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>(
      `${this.environmentService.getApiUrl()}/api/races/delete`, race.id
    ).subscribe(actionResult => {
      this.loaderService.dismissLoading();
      actionResult.resultCode !== -1 ? onSuccess() : onError();
    });
  }

  findRace = (raceRequest: FetchRaceByIdRequest, onSuccess: () => void, onError: () => void) =>
    this.http.post<ActionResult<Race>>(
      `${this.environmentService.getApiUrl()}/api/public/races/getracebyid`, raceRequest
    ).subscribe(actionResult => this.currentRace.next(actionResult.result));

  registerToRace = (isAdmin: boolean, registerRaceRequest: RaceRegistrationRequestDto, registrationWithoutPayment: boolean, onSuccess: (registerToRaceResponse: RaceRegistrationResultDto) => void, onError: (error: RegisterToRaceError) => void) => {
    this.loaderService.startLoading();
    const url = isAdmin ? `/api/races/organiser/register?withoutPayment=${registrationWithoutPayment}`
        : this.authenticationService.isAuthenticated() ? "/api/races/registertorace" : "/api/public/races/registertorace";
    this.http.post<ActionResult<any>>(
      `${this.environmentService.getApiUrl()}${url}`, registerRaceRequest
    ).pipe(tap(() => this.loaderService.dismissLoading())).subscribe(actionResult => {
      if (actionResult.resultCode === RegisterToRaceError.USER_ALREADY_REGISTERED_TO_RACE) {
        onError(RegisterToRaceError.USER_ALREADY_REGISTERED_TO_RACE);
      } else if (!actionResult.success) {
        onError(RegisterToRaceError.REGISTERED_TO_RACE_ERROR);
      } else {
        onSuccess(actionResult.result!);
      }
    }, () => {
      this.loaderService.dismissLoading();
      onError(RegisterToRaceError.REGISTERED_TO_RACE_ERROR);
    });
  }

  calculateRaceItemPrices = (registerRaceRequest: RaceRegistrationRequestDto, onSuccess: (registerToRaceResponse: RaceRegistrationResultDto) => void, onError: () => void) => {
    this.loaderService.startLoading();
    const url = this.authenticationService.isAuthenticated() ? "/api/races/calculate" : "/api/public/races/calculate";
    this.http.post<ActionResult<any>>(
      `${this.environmentService.getApiUrl()}${url}`, registerRaceRequest
    ).pipe(tap(() => this.loaderService.dismissLoading())).subscribe(actionResult => {
      actionResult.success ? onSuccess(actionResult.result!) : onError();
    });
  }

  emptyRaces = () => {
    this.currentRace.next(undefined);
    this.races.next(null);
  }

  findMatchingRegistrations = (filter: PagedFilter<RegistrationFilter> | undefined): Observable<RaceRegistration[]> =>
    this.http.post<ActionResultList<RaceRegistration[], number>>(
      `${this.environmentService.getApiUrl()}/api/organiser/registrations`, filter
    ).pipe(map(actionResult => actionResult.resultCode !== -1 && actionResult.result ? actionResult.result : []));

  fetchRaceToCopy = (raceRequest: FetchRaceByIdRequest): Observable<Race | null> =>
    this.findRaceByIdProtected(raceRequest).pipe(map(race => {
      if (race) {
        race.status = RaceStatus.INACTIVE;
        race.raceStart = race.raceEnd = race.registrationEnd = race.registrationStart = undefined;
        return race;
      }
      return null;
    }));

  findRaceByIdInner = (raceRequest: FetchRaceByIdRequest): Observable<Race | null> =>
    this.http.post<ActionResult<Race>>(
      `${this.environmentService.getApiUrl()}/api/public/races/getracebyid`, raceRequest
    ).pipe(map(value => {
      if (value?.result) {
        this.currentRace.next(value.result);
        return value.result;
      }
      return null;
    }));

  findRaceById = (raceRequest: FetchRaceByIdRequest): Observable<Race | null> =>
    forkJoin({
      race: this.findRaceByIdInner(raceRequest),
      raceSettings: this.raceSettingsService.fetchRaceSettings(raceRequest.raceId),
      applicationFormSettings: this.applicationFormSettingsService.fetchApplicationFormSettings(raceRequest.raceId)
    }).pipe(map(data => this.combineRaceData(data)));

  findRaceByIdProtectedInner = (raceRequest: FetchRaceByIdRequest): Observable<Race | null> =>
    this.http.post<ActionResult<Race>>(
      `${this.environmentService.getApiUrl()}/api/races/getracebyid`, raceRequest
    ).pipe(map(value => value?.result ?? null));

  findRaceByIdProtected = (raceRequest: FetchRaceByIdRequest, loading = false): Observable<Race | null> => {
    if (loading) this.loaderService.startLoading();
    return forkJoin({
      race: this.findRaceByIdProtectedInner(raceRequest),
      raceSettings: this.raceSettingsService.fetchRaceSettings(raceRequest.raceId),
      applicationFormSettings: this.applicationFormSettingsService.fetchApplicationFormSettings(raceRequest.raceId)
    }).pipe(tap(() => loading && this.loaderService.dismissLoading()), map(data => this.combineRaceData(data)));
  }

  /**
   * Updates ordinal numbers for a list of races by calling the backend endpoint.
   * @param races Array of Race objects to update
   * @param onSuccess Callback on success
   * @param onError Callback on error
   */
  updateOrdinalNumbersFromList = (
    races: Race[],
    onSuccess: () => void,
    onError: () => void
  ) => {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>(
      `${this.environmentService.getApiUrl()}/api/races/updateordinalnumbers`,
      races
    ).subscribe({
      next: (actionResult) => {
        this.loaderService.dismissLoading();
        if (actionResult.resultCode !== -1 && actionResult.result) {
          onSuccess();
        } else {
          onError();
        }
      },
      error: () => {
        this.loaderService.dismissLoading();
        onError();
      }
    });
  }

  private combineRaceData = (data: any): EventItem | null => {
    if (data.race) {
      data.race.raceSettings = data.raceSettings;
      data.race.applicationFormSettings = data.applicationFormSettings;
      return data.race;
    }
    return null;
  }

}

export enum RegisterToRaceError {
  USER_ALREADY_REGISTERED_TO_RACE = 10,
  REGISTERED_TO_RACE_ERROR
}
